import React from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { RegistrationFlowLayout } from "./RegistrationLayouts"
import { useTrackView } from "#/utils/heap"
import { Box, Stack, useTheme, dayjs } from "@carrotfertility/carotene-core"
import { Form, FormDatePicker } from "@carrotfertility/carotene-core-x"
import { RegistrationFlowTitle } from "./RegistrationFlowTitle"
import { ContinueButton, RegistrationButtonFooter, SkipButton } from "./RegistrationButtons"
import { validateOptionalDate } from "#/services/common-forms/validations"

interface DeliveryDateStepProps {
  deliveryDate: string | null
  onBack: () => void
  onComplete: (formValues: { deliveryDate: string | null }) => void
  employeeId: number
}

export function DeliveryDateStep({ deliveryDate, onBack, onComplete, employeeId }: DeliveryDateStepProps): JSX.Element {
  useTrackView("DeliveryDateStepRegFlow", "Delivery date step")
  const theme = useTheme()
  const intl = useIntl()

  const header = intl.formatMessage({
    defaultMessage: "When did the birth take place?",
    description: "This relates to a pregnancy delivery date (birth)"
  })

  const onSubmit = (formValues: { deliveryDate?: dayjs.Dayjs }): void => {
    const formattedDate = formValues.deliveryDate ? formValues.deliveryDate.format("YYYY-MM-DD") : null
    onComplete({ deliveryDate: formattedDate })
  }

  const defaultValue = deliveryDate ? dayjs(deliveryDate) : null

  return (
    <>
      <RegistrationFlowTitle title={header} />
      <RegistrationFlowLayout
        header={header}
        description={intl.formatMessage({
          defaultMessage: "This information will help us provide you with the best possible support."
        })}
        interactiveElements={
          <Form onSubmit={onSubmit} defaultValues={{ deliveryDate: defaultValue }}>
            <Box marginTop={(theme) => theme.spacing(theme.tokens.spacing.lg)}>
              <Stack spacing={theme.spacing(theme.tokens.spacing.sm)}>
                <FormDatePicker
                  name="deliveryDate"
                  controllerProps={{
                    rules: {
                      validate: (value: dayjs.Dayjs | null | undefined) => {
                        // Only validate if a date is provided (skip is allowed)
                        if (!value) {
                          return true
                        }
                        const result = validateOptionalDate(value)
                        if (typeof result !== "boolean") {
                          return intl.formatMessage(result)
                        }
                        return result
                      }
                    }
                  }}
                />
              </Stack>
              <RegistrationButtonFooter
                onBack={onBack}
                skipButton={
                  <SkipButton onClick={() => onComplete({ deliveryDate: null })}>
                    <FormattedMessage defaultMessage="Skip" />
                  </SkipButton>
                }
                continueButton={
                  <ContinueButton>
                    <FormattedMessage defaultMessage="Continue" />
                  </ContinueButton>
                }
              />
            </Box>
          </Form>
        }
        employeeId={employeeId}
      />
    </>
  )
}
